import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { cn } from "@/lib/utils";

interface MarkdownRendererProps {
    content: string;
    className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
    return (
        <div className={cn("prose max-w-none", className)}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                    // Headings
                    h1: ({ children }) => (
                        <h1 className="text-2xl font-bold text-primary mb-4 pb-2 border-b border-outline-variant/20">
                            {children}
                        </h1>
                    ),
                    h2: ({ children }) => <h2 className="text-xl font-bold text-primary mb-3 mt-6">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-semibold text-primary mb-2 mt-4">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-md font-semibold text-primary mb-2 mt-3">{children}</h4>,
                    h5: ({ children }) => <h5 className="text-sm font-semibold text-primary mb-1 mt-2">{children}</h5>,
                    h6: ({ children }) => <h6 className="text-sm font-medium text-primary mb-1 mt-2">{children}</h6>,

                    // Paragraphs
                    p: ({ children }) => <p className="text-on-surface mb-3 leading-relaxed">{children}</p>,

                    // Lists
                    ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-3 text-on-surface space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                        <ol className="list-decimal list-inside mb-3 text-on-surface space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => <li className="text-on-surface ml-2">{children}</li>,

                    // Tables
                    table: ({ children }) => (
                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border-collapse border border-outline-variant/20 bg-surface-container-low rounded-lg">
                                {children}
                            </table>
                        </div>
                    ),
                    thead: ({ children }) => <thead className="bg-surface-container-high">{children}</thead>,
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => <tr className="border-b border-outline-variant/20 hover:bg-surface-container/50">{children}</tr>,
                    th: ({ children }) => (
                        <th className="border border-outline-variant/20 px-4 py-2 text-left font-semibold text-primary bg-surface-container-high">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="border border-outline-variant/20 px-4 py-2 text-on-surface">{children}</td>
                    ),

                    // Links
                    a: ({ href, children }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80 transition-colors"
                        >
                            {children}
                        </a>
                    ),

                    // Code
                    code: ({ children, ...props }: { children?: React.ReactNode; inline?: boolean }) => {
                        const inline = "inline" in props ? props.inline : false;
                        if (inline) {
                            return (
                                <code className="bg-surface-container text-primary px-1.5 py-0.5 rounded text-sm font-mono">
                                    {children}
                                </code>
                            );
                        }
                        return (
                            <code className="text-sm font-mono" {...props}>
                                {children}
                            </code>
                        );
                    },

                    // Code blocks
                    pre: ({ children }) => (
                        <pre className="bg-surface-container-high border border-outline-variant/20 rounded-lg p-4 overflow-x-auto mb-4">
                            {children}
                        </pre>
                    ),

                    // Blockquotes
                    blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-primary pl-4 py-2 mb-4 bg-surface-container italic text-on-surface">
                            {children}
                        </blockquote>
                    ),

                    // Horizontal rules
                    hr: () => <hr className="border-outline-variant/20 my-6" />,

                    // Strong/Bold
                    strong: ({ children }) => <strong className="font-bold text-primary">{children}</strong>,

                    // Emphasis/Italic
                    em: ({ children }) => <em className="italic text-on-surface">{children}</em>,

                    // Images
                    img: ({ src, alt }) => (
                        <img src={src} alt={alt} className="max-w-full h-auto rounded-lg border border-outline-variant/20 mb-4" />
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
